import styled from "styled-components";
import { TextField, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ShareMuiIcon from "@mui/icons-material/Share";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import { theme } from "../../utils/globalStyles/colors";
import { fadeInUp } from "../../utils/globalStyles/animations";

export const Container = styled.div`
  width: 100%;
  max-width: 600px; /* Limita a largura em telas grandes */
  margin: 0 auto;
  padding: 24px 16px 8px; /* o espaço do menu inferior fica no BodyContainer */
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
`;

/* Cartão do perfil (mesmo gradiente do cartão de saldo da Carteira) */
export const ProfileCard = styled.div`
  width: 100%;
  background: linear-gradient(135deg, ${theme.colors.base}, #e68a47);
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 8px 24px rgba(211, 114, 40, 0.3);
  box-sizing: border-box;
  color: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: ${fadeInUp} 0.35s ease-out backwards;
`;

export const ProfileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

export const ProfileInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const ProfileName = styled(Typography)`
  color: #ffffff !important;
  font-weight: 800 !important;
  font-size: 19px !important;
  word-break: break-word;
`;

export const ProfileLogin = styled(Typography)`
  color: rgba(255, 255, 255, 0.8) !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CodeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  padding: 10px 12px;
  border-radius: 12px;
  background-color: rgba(255, 255, 255, 0.16);
`;

export const CodeInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

export const CodeLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.8);
`;

export const CodeValue = styled.span`
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 2px;
`;

export const CodeActions = styled.div`
  display: flex;
  gap: 6px;
`;

export const CodeAction = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 36px;
  padding: 0 12px;
  border: none;
  border-radius: 999px;
  background-color: #ffffff;
  color: ${theme.colors.base};
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.15s ease;

  &:active {
    transform: scale(0.95);
  }
`;

export const CopyIcon = styled(ContentCopyIcon)`
  font-size: 16px !important;
`;

export const CopiedIcon = styled(CheckIcon)`
  font-size: 16px !important;
  color: #28a745;
`;

export const ShareIcon = styled(ShareMuiIcon)`
  font-size: 16px !important;
`;

export const SectionTitle = styled.h2`
  margin: 28px 4px 10px;
  font-size: 13px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #888888;
`;

export const FormCard = styled.form`
  width: 100%;
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  padding: 20px 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const Field = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column; /* label acima do campo: leitura melhor no celular */
  gap: 6px;
`;

export const FieldLabel = styled.label`
  font-weight: 600;
  color: #555555;
  font-size: 14px;
`;

export const Input = styled(TextField)`
  width: 100%;

  & .MuiInputBase-root {
    background-color: #f9f9f9; /* destaca o campo dentro do card branco */
    border-radius: 10px;
  }

  & .MuiInputBase-input {
    font-size: 16px; /* >= 16px evita o zoom automático do iOS ao focar */
    padding: 12px 14px;
    color: #333333;
  }

  & input::placeholder {
    color: #aaaaaa;
    opacity: 1;
  }

  & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${theme.colors.base};
    border-width: 2px;
  }

  & .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline {
    border-color: ${theme.colors.error};
  }

  & .MuiFormHelperText-root {
    margin: 6px 2px 0;
    font-size: 12px;
  }
`;

export const SubmitButton = styled.button<{ $inactive: boolean }>`
  width: 100%;
  height: 50px;
  margin-top: 4px;
  border: none;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  cursor: ${({ $inactive }) => ($inactive ? "default" : "pointer")};
  background-color: ${({ $inactive }) =>
    $inactive ? "#e9ecef" : theme.colors.base};
  color: ${({ $inactive }) => ($inactive ? "#aaaaaa" : "#ffffff")};
  box-shadow: ${({ $inactive }) =>
    $inactive ? "none" : "0 4px 12px rgba(192, 90, 33, 0.25)"};
  transition:
    background-color 0.2s ease,
    color 0.2s ease,
    transform 0.15s ease;

  &:active {
    transform: ${({ $inactive }) => ($inactive ? "none" : "scale(0.98)")};
  }
`;

export const LogoutButton = styled.button`
  width: 100%;
  height: 50px;
  margin: 28px 0 8px;
  border: 1px solid rgba(217, 48, 37, 0.35);
  border-radius: 12px;
  background-color: #ffffff;
  color: #d93025;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    transform 0.15s ease;

  &:active {
    transform: scale(0.98);
    background-color: rgba(217, 48, 37, 0.06);
  }
`;

export const LogoutIcon = styled(LogoutRoundedIcon)`
  font-size: 20px !important;
`;
